/* eslint-disable no-unused-vars */
import Packet from './Packet';
import ProjectServerConstants from '@/util/ProjectServerConstants';
import { PacketType } from './Packet';
import VueAxios from 'vue-axios';
import Vue from 'vue';
import store from '@/store/store';
import Connection from './Connection';
import Node from './Node';
import StringPacking from './StringPacking';
import Tag from './Tag';
import Timeline from './Timeline';
import TimelineAccess from './TimelineAccess';
import Vector2 from './Vector2';
import Guid from './Guid';
import { DelegateBuilder } from './DelegateBuilder';
import Mutations from '@/store/mutations';
import ChatMessage from './ChatMessage';
import DateConverter from '@/models/DateConverter';
import Project from './Project';
import project from '@/store/modules/project';
import router from '@/router';
import DateOptions from './DateOptions';
import AsyncRequests from './packets/AsyncRequest';

import { MapNodeCreateRequest, MapNodeEditRequest, MapNodeDestroy, MapNodeCreateResponse } from '@/models/packets/MapNode';
import { GetSID } from '@/session';
import { ProjectNotifyMetadataChangedResponse } from './packets/Project';
import { ProjectsBaseURL } from '@/constants/environment';
import { DeleteFolderData, FolderUpdateAccessData, SetFolderData, TimelineSetFolderData } from './packets/Folder';
import TimelineAccessUserData from './TimelineAccessUserData';
import Axios from 'axios';

async function forceRefreshBrowser()
{
    // Attempt to POST to the root and the current page to attempt to clear cache
    await Promise.all([Axios.post(window.location.origin).catch(()=>
    {}), Axios.post(window.location.href).catch(()=>
    {})]);

    // Pass the optional parameter for browsers that support it for extra assurance
    (window.location as any).reload(true);
}

(window as any).forceRefresh = forceRefreshBrowser;

export default class ProjectServer
{
    public static host : string = "cliovis.org";
    public static route : string | null = null;
    public static port : number = 3000;
    private requests = new AsyncRequests(this);

    get AsyncRequests()
    {
        return this.requests;
    }
    
    private static _inst : ProjectServer;
    public IsRequestingAddress : Boolean = false;

    static get instanceExists() : boolean
    {
        return ProjectServer._inst != null;
    }

    public static isActiveInstance(serv : ProjectServer)
    {
        return ProjectServer._inst == serv;
    }

    public static get instance() : ProjectServer
    {
        if (ProjectServer._inst == null)
        {
            ProjectServer._inst = new ProjectServer();
        }
        return ProjectServer._inst;
    }

    public get getting_address() : Boolean
    {
        return this.IsRequestingAddress;
    }

    public get connecting() : Boolean
    {
        return this.getting_address || (this.socket != null && this.socket.readyState == WebSocket.CONNECTING);
    }

    public get connected() : boolean
    {
        return this.socket != null && this.socket.readyState == WebSocket.OPEN;
    }

    public socket : WebSocket = <WebSocket><unknown>null;
    public onConnectionOpened : DelegateBuilder = new DelegateBuilder();
    public onConnectionClosed : DelegateBuilder = new DelegateBuilder();

    private localUserId : number = -1;
    public nodesReceived : number = 0;
    public nodesToReceive : number = -1;
    public tagsReceived : number = 0;
    public tagsToReceive :number = -1;

    private relatives = new Map<number, any[]>();
    private nodetags = new Map<number, number[]>();

    private _callbacks = new Map<PacketType, Function[]>();
    private getTypeListIfExists(type : PacketType) : Function[] | undefined
    {
        return this._callbacks.get(type);
    }
    
    private getOrCreateTypeList(type : PacketType) : (Function | null)[]
    {
        let list = this._callbacks.get(type);
        if (list == null)
        {
            this._callbacks.set(type, []);
            list = this._callbacks.get(type);
            if (list == null)
                throw "Failed to register local callback - could not allocate list";
        }
        return list;
    }

    /**
     * Registers a callback for a given packet type.  Returns an ID >= 0 on success.  That ID should be passed to removeCallback when the callback is destroyed.
     * NOTE: IDs are currently reused upon removal.  At some point, we should make it so that the upper bits correspond to a generation index for safety.
     * @param type PacketType to listen for
     * @param cb Callback invoked whenever a packet of the given type is received.  Arguments are as follows: (data : any, packet : Packet)
     */
    public registerCallback(type : PacketType, cb : Function) : number
    {
        const list = this.getOrCreateTypeList(type);
        
        for(let i = 0; i < list.length; ++i)
        {
            if (list[i] == null)
            {
                list[i] = cb;
                return i;
            }
        }

        const id = list.length;
        list.push(cb);
        return id;
    }

    /**
     * Removes a callback registered with registerCallback.  Pass in the ID returned by registerCallback.  Returns true on success, false on failure;
     * @param type Packet type for the callback
     * @param id ID returned by registerCallback to remove.
     */
    public removeCallback(type : PacketType, id : number)
    {
        const list = this.getOrCreateTypeList(type);
        if (id < list.length)
        {
            list[id] = null;
            
            // Shrink the array if we removed the last element
            // list.length > 0 check should be moot, but meh
            if (list.length > 0 && !list[list.length - 1])
            {
                console.log('shrinking callback array');
                list.length--;
            }
            return true;
        }
        return false;
    }

    private _handlers = new Map<PacketType, DelegateBuilder>();
    public get handlers() : Map<PacketType, DelegateBuilder>
    {
        if (this._handlers === undefined || this._handlers === null)
        {
            this._handlers = new Map<PacketType, DelegateBuilder>();
        }
        return this._handlers;
    }

    public disconnect() : void
    {
        this.intentionalClosure = true;
        this.IsRequestingAddress = false;
        if (this.connected)
        {
            this.socket.close();
        }
        this.socket = <WebSocket><unknown>null;
        
        if (ProjectServer.isActiveInstance(this))
        {
            ProjectServer._inst = <ProjectServer><unknown>null;
        }
    }

    // Interval for rate limiting.  Defaults to 250ms.
    public rateLimitDurationMS = 250;

    public connectToServer() : void
    {
        if (this.socket == null)
        {
            let url = '';

            // TODO: Set this up in a more robust way
            if (ProjectServer.route === "DOCKER")
            {
                url = `ws://localhost:5000/`;
            }
            else
            {
                url = `${ProjectsBaseURL}${ProjectServer.route}`;
            }

            console.log("Connecting to " + url + "...");
            this.socket = new WebSocket(url);
            this.socket.onopen = () =>
            {
                if (this.outInterval != null)
                {
                    clearInterval(this.outInterval);
                }

                // TODO: Dynamic interval?
                const removeList : number[] = [];
                let lastSend = new Date(1, 1);
                this.outInterval = setInterval(()=>
                {
                    if (this.outQueue.length == 0) return;
                    
                    const now = new Date();
                    if ((now.getTime() - lastSend.getTime()) < this.rateLimitDurationMS) return;
                    lastSend = now;

                    console.log(`Processing ${this.outQueue.length} packets...`);
                    console.log(this.outQueue);
                    
                    removeList.length = 0;
                    for(let i = 0; i < this.outQueue.length; ++i)
                    {
                        const pack = this.outQueue[i];
                        const simp = ProjectServer.packetSimplificationHandlers.get(pack.type);
                        if (simp)
                        {
                            simp(this.outQueue, i, pack, removeList);
                            
                            if (removeList.length > 0)
                            {
                                // TODO: This sort may be unnecessary
                                removeList.sort();
                                console.log(this.outQueue);
                                for(let i = removeList.length - 1; i >= 0 ; --i)
                                {
                                    console.log(`REMOVE ${removeList[i]}`);
                                    this.outQueue.splice(removeList[i], 1);
                                }
                                console.log(this.outQueue);
                                removeList.length = 0;
                            }
                        }
                    }
                    
                    console.log(`Sending ${this.outQueue.length} packets...`);
                    for(const pack of this.outQueue)
                    {
                        console.log(`Sending packet of type ${pack.type}`);
                        this.sendPacketImmediate(pack);
                    }
                    this.outQueue.length = 0;
                }, 60);

                console.log("Connected: " + this.connected);
                this.onConnectionOpened.invoke(this);
                this.socket.onmessage = (ev : MessageEvent)=>
                {
                    this.dataReceived(ev);
                };
                const sid = GetSID();
                this.sendData(PacketType.FullSyncRequest, { "sid" : sid, version: ProjectServerConstants.ProtocolVersion });
            };
            
            this.socket.onclose = ()=>
            {
                if (this.outInterval != null)
                {
                    clearInterval(this.outInterval);
                    this.outInterval = null;
                }
                console.log("Connection closed");
                this.onConnectionClosed.invoke(this);
                console.log("ran delegates");
            };
        }
    }

    private intentionalClosure = false;
    public get WasIntentionalClosure()
    {
        return this.intentionalClosure;
    }

    private static packetSimplificationHandlers = new Map<PacketType, (array : Packet[], index : number, pack : Packet, outArray : number[])=>void>();
    public static setPacketTypeSimplifier(type : PacketType, fn : (array : Packet[], index : number, pack : Packet, outArray : number[])=>void)
    {
        ProjectServer.packetSimplificationHandlers.set(type, fn);
    }
    
    private outInterval : any = null;
    private outQueue : Packet[] = [];
    public sendData(type : PacketType, data : any = null) : void
    {
        this.sendPacket(new Packet(type, data));
    }

    public sendPacket(data : Packet) : void
    {
        if (ProjectServer.packetSimplificationHandlers.has(data.type))
        {
            this.outQueue.push(data);
        }
        else
        {
            this.sendPacketImmediate(data);
        }
    }

    public sendPacketImmediate(data : Packet) : void
    {
        if (this.connected)
        {
            this.socket.send(JSON.stringify(data));
        }
    }

    private timelineSyncHasRunFinishedLogic : boolean = false;

    private hasRunFinishedLogic : boolean = false;
    public get projectReady() : boolean
    {
        return this.hasRunFinishedLogic;
    }

    private timelineSyncNodeDoneStuff() : void
    {
        if (this.timelineSyncHasRunFinishedLogic)
            return;

        this.timelineSyncHasRunFinishedLogic = true;

        store.commit(Mutations.timelineSyncNodesPostProcess, { 
            relatives: this.relatives, 
            nodetags: this.nodetags 
        });
    }

    public dataReceived(ev : MessageEvent)
    {
        if (!ProjectServer.isActiveInstance(this))
        {
            this.disconnect();
            return;
        }

        const packet = JSON.parse(ev.data) as Packet;
        if (packet.type != undefined)
        {
            const type = packet.type as PacketType;
            const data = packet.data;

            //console.log(type);
            let callbackArg = null;
            switch (type)
            {
            case PacketType.UpdateRateLimitInterval:
                if (typeof data === "number")
                {
                    console.log(`Updating rate limit to ${data}`);
                    this.rateLimitDurationMS = data;
                }
                else
                {
                    console.error(`Invalid rate limit received (${data})`);
                }
                break;
            case PacketType.Chat:
                if (data.sender == this.localUserId)
                    console.log("[ME] User " + data.sender + ": " + data.text);
                else
                    console.log("User " + data.sender + ": " + data.text);

                const msg = new ChatMessage;
                msg.channel = data.channel;
                msg.sender = data.sender;
                msg.senderId = data.senderId;
                msg.timestamp = data.timestamp;
                msg.text = data.text;
                msg.messageid = data.messageid;
                store.commit(Mutations.chatMessage, msg);
                break;
            case PacketType.LocalUserConnect:
                this.localUserId = data;
                break;
            case PacketType.UserConnect:
                console.log("User " + data + " connected!");
                break;
            case PacketType.UserDisconnect:
                console.log("User " + data + " disconnected :(");
                break;
            case PacketType.TimelineSyncNodesInitResponse:
                this.timelineSyncHasRunFinishedLogic = false;
                store.commit(Mutations.setTimelineSyncing, true);
                this.nodesReceived = 0;
                this.nodesToReceive = data.nodeCount;
                console.log("receiving " + this.nodesToReceive + " nodes");
                this.relatives = new Map<number, Connection[]>();
                this.nodetags = new Map<number, number[]>();
                if (this.nodesReceived == this.nodesToReceive)
                {
                    this.timelineSyncNodeDoneStuff();
                }
                break;
            case PacketType.TimelineSyncTagsInitResponse:
                this.tagsToReceive = data;
                this.tagsReceived = 0;
                break;
            case PacketType.TimelineSyncNodesResponse:
                let nodeid : number = data.nodeid;
                let node : Node = <Node><unknown>null;

                let nodeName : string = data.name;
                let typeChar : string = nodeName.substring(0,1);
                nodeName = nodeName.substring(1);

                const color : any = (data.color != null && data.color.r != null) ? data.color : null;
                node = store.getters.nodes.get(nodeid);
                if (node == undefined)
                {
                    node = new Node(nodeid, DateConverter.serverInputToDate(data.start), color);
                }
                else
                {
                    node.nodeid = nodeid;
                    node.dateStart = DateConverter.serverInputToDate(data.start);
                    if (color == null)
                        node.computeEraColor();
                    else
                        node.color = color;
                }

                node.name = nodeName;
                node.type = (Node.charToType(typeChar) == Node.NodeType.TimelineEra) ? Node.NodeType.TimelineEra : Node.NodeType.TimelineEvent;
                    
                node.important = (data.important == 1);

                const tup = StringPacking.Split(data.description);
                node.description = tup[0];
                if (tup.length > 1)
                {
                    node.references = tup[1];
                }
                if (data.image != undefined && data.image != null && data.image != "")
                {
                    // TODO: URI verification
                    node.image = data.image;
                }
                if (data.video != undefined && data.video != null && data.video != "")
                {
                    // TODO: URI verification
                    node.video = data.video;
                }
                if (data.imageCaption != null)
                {
                    node.imageCaption = data.imageCaption;
                }
                if (data.audio != undefined && data.audio != null && data.audio != "")
                {
                    console.log(`node ${node.nodeid} reading audio => ${data.audio}`);
                    node.audio = data.audio;
                }
                    
                node.dateEnd = DateConverter.serverInputToDate(data.end);
                node.dateOptions = new DateOptions(data.dateOptions);

                if (this.relatives.has(node.nodeid))
                {
                    console.log("Warning: Overwriting relatives for node " + node.nodeid);
                }
                this.relatives.set(node.nodeid, data.connections);
                    
                Vue.set(node, 'tags', []);
                this.nodetags.set(node.nodeid, data.tags);

                node.data = JSON.parse(data.data);

                store.commit(Mutations.setNode, node);

                this.nodesReceived++;

                if (this.nodesReceived == this.nodesToReceive)
                {
                    this.timelineSyncNodeDoneStuff();
                }

                callbackArg = node;
                break;
            case PacketType.TimelineSyncTagsResponse:
                const tag = new Tag(data.tagid, data.name, data.type, data.color);

                store.commit(Mutations.setTag, tag);

                this.tagsReceived++;

                callbackArg = tag;
                break;
            case PacketType.TimelineSyncNodesFail:
                console.log("oh nooooodes");
                break;
            case PacketType.TimelineSyncTagsFail:
                console.log("tags failed!");
                break;
            case PacketType.TimelineSyncNodesDone:
                // TEMPORARY: Moved to end of TimelineSyncNodesResponse
                if (this.nodesReceived == this.nodesToReceive)
                {
                    this.timelineSyncNodeDoneStuff();
                }
                // TODO: implement in context specific message handler
                break;
            case PacketType.TimelineSyncTagsDone:
                break;
            case PacketType.NodeUpdateTitle:
                nodeid = data.nodeid;
                const dumbCharCodeThingThatWeShouldReallyGetRidOf = data.value.charCodeAt(0);
                if (dumbCharCodeThingThatWeShouldReallyGetRidOf > 139 && dumbCharCodeThingThatWeShouldReallyGetRidOf < 143)
                {
                    data.value = data.value.substring(1);
                }
                store.commit(Mutations.updateNodeTitle, data);
                callbackArg = nodeid; // TODO: can't get nodes right now when the store is imported outside a component, passing nodeid is probably more efficient anyway
                break;
            case PacketType.NodeSetImportant:
                nodeid = data.nodeid;
                store.commit(Mutations.updateNodeImportant, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeUpdateDescription:
            {
                nodeid = data.nodeid;
                const tup = StringPacking.Split(data.value); // TODO: this hack needs to go.
                store.commit(Mutations.updateNodeDescription, {nodeid: nodeid, value: tup[0]});
                    
                if (tup.length > 1)
                {
                    store.commit(Mutations.updateNodeReferences, {nodeid: nodeid, value: tup[1]});
                    //store.commit(Mutations.updateNodeReferences, tup[1]);
                }
                callbackArg = nodeid;
                break;
            }
            case PacketType.NodeUpdateStart:
                nodeid = data.nodeid;
                data.value = DateConverter.serverInputToDate(data.value);
                store.commit(Mutations.updateNodeStart, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeUpdateEnd:
                nodeid = data.nodeid;
                data.value = DateConverter.serverInputToDate(data.value);
                store.commit(Mutations.updateNodeEnd, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeUpdateDateOptions:
                nodeid = data.nodeid;
                store.commit(Mutations.updateNodeDateOptions, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeUpdateImageCaption:
                nodeid = data.nodeid;
                store.commit(Mutations.updateNodeImageCaption, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeUpdateImage:
                nodeid = data.nodeid;
                store.commit(Mutations.updateNodeImage, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeUpdateVideo:
                nodeid = data.nodeid;
                store.commit(Mutations.updateNodeVideo, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeAddConnection:
            {
                nodeid = data.nodeid;
                const otherid = data.otherid;

                const connection = new Connection(nodeid, otherid);

                store.commit(Mutations.setConnection, connection);
                    
                callbackArg = nodeid;
                break;
            }
            case PacketType.NodeRemoveConnection:
            {
                nodeid = data.nodeid;
                const otherid = data.otherid;

                const connection = new Connection(nodeid, otherid);

                store.commit(Mutations.removeConnection, connection);
                    
                callbackArg = nodeid;
                break;
            }
            case PacketType.NodeAddTag:
                nodeid = data.nodeid;
                store.commit(Mutations.addNodeTag, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeRemoveTag:
                nodeid = data.nodeid;
                store.commit(Mutations.removeNodeTag, data);
                callbackArg = nodeid;
                break;
            case PacketType.TimelineAddTag:
                let timelineid = data.timelineid;
                store.commit(Mutations.addTimelineTag, data);
                callbackArg = timelineid;
                break;
            case PacketType.TimelineRemoveTag:
                timelineid = data.timelineid;
                store.commit(Mutations.removeTimelineTag, data);
                callbackArg = timelineid;
                break;
            case PacketType.NodeCreate:
            {
                nodeid = data.nodeid;
                node = <Node><unknown>null;
                console.log(`Node created: ${JSON.stringify(data)}`);
                nodeName = data.name;
                typeChar = nodeName.substring(0,1);
                nodeName = nodeName.substring(1);

                node = store.getters.nodes.get(nodeid);
                if (node == undefined)
                {
                    node = new Node(nodeid, DateConverter.serverInputToDate(data.start));
                }
                else
                {
                    node.nodeid = nodeid;
                    node.dateStart = DateConverter.serverInputToDate(data.start);
                    node.computeEraColor();
                }
                    
                node.name = nodeName;
                node.type = (Node.charToType(typeChar) == Node.NodeType.TimelineEra) ? Node.NodeType.TimelineEra : Node.NodeType.TimelineEvent;
                node.important = (data.important == 1);

                const tup = StringPacking.Split(data.description);
                node.description = tup[0];
                if (tup.length > 1)
                {
                    node.references = tup[1];
                }
                if (data.image != undefined && data.image != null && data.image != "")
                {
                    // TODO: URI verification
                    node.image = data.image;
                }
                if (data.imageCaption != null)
                {
                    node.imageCaption = data.imageCaption;
                }
                if (data.audio != undefined && data.audio != null && data.audio != "")
                {
                    // TODO: URI verification
                    node.audio = data.audio;
                }
                if (data.video != undefined && data.video != null && data.video != "")
                {
                    // TODO: URI verification
                    node.video = data.video;
                }

                node.dateEnd = DateConverter.serverInputToDate(data.end);
                node.dateOptions = new DateOptions(data.dateOptions);

                Vue.set(node, 'tags', []);
                
                console.log(data.data);
                if (data.data) node.data = JSON.parse(data.data);

                store.commit(Mutations.setNode, node);

                callbackArg = node;
                break;
            }
            case PacketType.NodeDestroy:
                nodeid = data.nodeid;
                store.commit(Mutations.deleteNode, data);
                callbackArg = nodeid;
                break;
            case PacketType.NodeUpdateColor:
                nodeid = data.nodeid;
                store.commit(Mutations.updateNodeColor, data);
                callbackArg = nodeid;
                break;
            case PacketType.TagCreate:
            {
                const tag = new Tag(data.tagid, data.name, data.type, data.color);
                store.commit(Mutations.setTag, tag);
                callbackArg = tag;
                break;
            }
            case PacketType.TagDestroy:
                store.commit(Mutations.deleteTag, data);
                callbackArg = data.tagid;
                break;
            case PacketType.TagUpdateTitle:
                store.commit(Mutations.updateTagTitle, data);
                callbackArg = data.tagid;
                break;
            case PacketType.TagUpdateColor:
                store.commit(Mutations.updateTagColor, data);
                callbackArg = data.tagid;
                break;
            case PacketType.UpdateConnectionText:
            {
                nodeid = data.nodeone;
                const otherid = data.nodetwo;
                const text = data.value;

                const connection = new Connection();
                connection.nodeid1 = Math.min(nodeid, otherid);
                connection.nodeid2 = Math.max(nodeid, otherid);
                const tup = StringPacking.Split(text);
                connection.description = tup[0];
                if (tup.length > 1)
                {
                    connection.references = tup[1];
                }

                store.commit(Mutations.updateConnectionText, connection);
                break;
            }
            case PacketType.UpdateConnectionDirection:
            {
                nodeid = data.nodeone;
                const otherid = data.nodetwo;
                const direction = data.value;

                const connection = new Connection();
                connection.nodeid1 = Math.min(nodeid, otherid);
                connection.nodeid2 = Math.max(nodeid, otherid);
                connection.direction = direction;

                store.commit(Mutations.updateConnectionDirection, connection);
                break;
            }
            case PacketType.TimelineUpdateTitle:
                timelineid = data.timelineid;
                store.commit(Mutations.updateTimelineTitle, data);
                callbackArg = timelineid;
                break;
            case PacketType.TimelineUpdateDescription:
                timelineid = data.timelineid;
                store.commit(Mutations.updateTimelineDescription, data);
                callbackArg = timelineid;
                break;
            case PacketType.TimelineUpdateComments:
                timelineid = data.timelineid;
                store.commit(Mutations.updateTimelineComments, data);
                callbackArg = timelineid;
                break;
            case PacketType.TimelineCreate:
            {
                const tl = new Timeline();
                tl.name = data.name;
                tl.description = data.description;
                tl.id = data.timelineid;
                console.log("Created timeline " + tl.name);

                const access : TimelineAccess = TimelineAccess.fromJsonObject(data.access);
                console.log(`[TimelineCreate] Setting access for user ${access.User.userid} to ${access.Permission} for timeline ${tl.name} [${tl.id}]`);
                tl.accessList.set(access.User.userid.toLowerCase(), access);

                store.commit(Mutations.setTimeline, tl);
                store.commit(Mutations.setMapToTimeline, data);
                callbackArg = tl;
                break;
            }
            case PacketType.TimelineDestroy:
                console.log("TimelineDestroy packet type should never be sent or received. TimelineDelete should be used instead. Eventually this packet number should be given to something else.");
                break;
            case PacketType.TimelineDelete:
                timelineid = data.timelineid;
                store.commit(Mutations.deleteTimeline, data);
                callbackArg = timelineid;
                break;
            case PacketType.TimelineChangeMapPin:
                timelineid = data.timelineid;
                store.commit(Mutations.setMapToTimeline, data);
                callbackArg = timelineid;
                break;
            case PacketType.TimelineAddNode:
                nodeid = data.nodeid;
                store.commit(Mutations.addTimelineNode, data);
                callbackArg = nodeid;
                break;
            case PacketType.TimelineRemoveNode:
                nodeid = data.nodeid;
                store.commit(Mutations.removeTimelineNode, data);
                callbackArg = nodeid;
                break;
            case PacketType.SetLocalUserData: // TODO: is this still necessary?
                store.commit(Mutations.setLocalUserData, data);
                break;
            case PacketType.FullSyncTimelinesRequest:
                break;
            case PacketType.FullSyncTimelinesInitResponse:
                store.commit(Mutations.fullSyncTimelinesInit);
                break;
            case PacketType.ForceCloseConnection:
            {
                const currentProject : Project | undefined = (store.state as any).project.project;
                console.log("Received forced disconnect request");
                this.disconnect();

                const reason = packet.data.reason;
                if (reason)
                {
                    alert(reason);
                }

                if (packet.data.reconnect && currentProject)
                {
                    // TODO: Create a route that connects to a given PID after an optional delay
                    // Delay is in seconds
                    const delay : number | undefined = packet.data.reconnect_delay;
                    if (delay && delay > 0)
                    {
                        router.push(`/app/reconnect/${currentProject.id}/${delay}`);
                    }
                    else
                    {
                        router.push(`/app/connect/${currentProject.id}`);
                    }
                }

                if (packet.data.refresh)
                {
                    forceRefreshBrowser();
                }
                break;
            }
            case PacketType.FullSyncTimelinesResponse:
            {
                const tl = new Timeline();
                tl.name = data.name;
                tl.description = data.description;
                tl.comments = data.comments;
                tl.id = data.timelineid;
                tl.folderid = data.folderid;
                    
                const tags = data.timelinetags as number[];
                if (tags)
                {
                    for (const tagid of tags)
                    {
                        // TODO: should this be using the addTimelineTag mutation?
                        const tag = ((store.state as any).project.globalTags as Map<number, Tag>).get(tagid);
                        if (tag)
                        {
                            tl.tags.push(tag);
                            tag.timelineCount += 1;
                        }
                        else
                        {
                            alert(`Error when connecting to project: Invalid tag in timeline {}`);
                            window.location.reload();
                            return;
                        }
                    }
                }

                console.log("Created timeline " + tl.name);

                const access = TimelineAccess.fromJsonObject(data.access);
                console.log("[TimelineCreate] access: " + access);
                    
                store.commit(Mutations.setTimeline, tl);
                store.commit(Mutations.setMapToTimeline, data);

                // Converting the access list to correctly initialized objects with the mutation
                {
                    const list = [];
                    list.push(access);
                    for(const val of tl.accessList)
                    {
                        list.push(val[1]);
                    }
                    store.commit(Mutations.setTimelineAccessList, { timelineid: tl.id, accesslist: list });
                }
                    
                callbackArg = tl;
                break;
            }
            case PacketType.TimelineSyncTimelineTags:
                timelineid = data.timelineid;
                store.commit(Mutations.timelineSyncTimelineTags, data);
                callbackArg = timelineid;
                break;
            case PacketType.TimelineSyncedTimelines:
                const accessedTimelines = data.timelines as number[];
                if (accessedTimelines)
                {
                    store.commit(Mutations.setAccessedTimelines, accessedTimelines);
                    callbackArg = accessedTimelines;
                }
                break;
            case PacketType.FullSyncTimelinesDone:
                this.hasRunFinishedLogic = true;
                break;
            case PacketType.FullSyncTimelinesFail:
                console.log("The cake is a (time)lie(n).");
                break;
            case PacketType.TimelineSyncTimelineNodesRequest:
                break;
            case PacketType.TimelineSyncTimelineNodesInitResponse:
                break;
            case PacketType.TimelineSyncTimelineNodesResponse:
                store.commit(Mutations.addTimelineNode, data);
                callbackArg = data.nodeid;
                break;
            case PacketType.TimelineSyncTimelineNodesDone:
                console.log("timeline nodes done");
                break;
            case PacketType.TimelineSyncTimelineNodesFail:
                console.log("timeline nodes fail ):");
                break;
            case PacketType.FullSyncMapNodesRequest:
                break;
            case PacketType.FullSyncMapNodesInitResponse:
                this.hasRunFinishedLogic = false;
                store.commit(Mutations.fullSyncMapNodesInit);
                break;
            case PacketType.FullSyncMapNodesResponse:
            {
                const tempPos : string = data.pos;
                const mnode = new Node(data.mapnodeid, new Date());
                mnode.type = Node.NodeType.MapNode;
                mnode.name = data.name;
                mnode.position = Vector2.parse(tempPos);
                store.commit(Mutations.setMapNode, mnode);
                callbackArg = mnode;
                break;
            }
            case PacketType.FullSyncMapNodesDone:
                break;
            case PacketType.FullSyncMapNodesFail:
                break;
            case PacketType.Ping:
                this.sendData(PacketType.Pong);
                break;
            case PacketType.Pong:
                break;
            case PacketType.DeleteChat:
                store.commit(Mutations.deleteChatMessage, data);
                break;
            case PacketType.TimelineUpdateAccess:
            {
                // TODO: remove logs when confirmed working
                const access = data.access;
                console.log("uid: " + access.User.userid + " user: " + access.User.uiName + " email: " + access.User.email + " perm: " + access.Permission);
                store.commit(Mutations.setTimelineAccess, data);
                callbackArg = data.timelineid;
                break;
            }
            case PacketType.GetTimelineAccessList:
                // TODO: remove logs when confirmed working
                console.log("accesslist count: " + data.accesslist.length);
                store.commit(Mutations.setTimelineAccessList, data);
                callbackArg = data.timelineid;
                break;
            case PacketType.TimelineSyncTimelineEventOrderingRequest:
                break;
            case PacketType.TimelineSyncTimelineEventOrderingInitResponse:
                console.log("TimelineSyncTimelineEventOrderingInitResponse");
                break;
            case PacketType.TimelineSyncTimelineEventOrderingResponse:
                store.commit(Mutations.setTimelineEventOrdering, data);
                callbackArg = data.timelineid;
                break;
            case PacketType.TimelineSyncTimelineEventOrderingDone:
                console.log("TimelineSyncTimelineEventOrderingDone");
                store.commit(Mutations.setTimelineSyncing, false);
                break;
            case PacketType.TimelineSyncTimelineEventOrderingFail:
                console.log("TimelineSyncTimelineEventOrderingFail");
                break;
            case PacketType.TimelineUpdateEventOrdering:
                store.commit(Mutations.setTimelineEventOrdering, data);
                callbackArg = data.timelineid;
                break;
            case PacketType.NodeUpdateAudio:
                nodeid = data.nodeid;
                store.commit(Mutations.updateNodeAudio, data);
                callbackArg = nodeid;
                break;
            case PacketType.ChangeMapData:
            {
                // TODO: Mutation/?
                //((store.state as any).project.project as Project).data = data.data;
                store.commit(Mutations.updateMapData, data.data);
                break;
            }
            case PacketType.MapNodeCreate:
            {
                const create = data as MapNodeCreateResponse;
                const mnode = new Node(create.id, new Date());
                mnode.type = Node.NodeType.MapNode;
                mnode.name = create.title;
                mnode.position = new Vector2(create.position.x, create.position.y);
                store.commit(Mutations.setMapNode, mnode);
                callbackArg = mnode;
                break;
            }
            case PacketType.MapNodeDestroy:
            {
                const destroy = data as MapNodeDestroy;
                store.commit(Mutations.destroyMapNode, destroy.id);
                break;
            }
            case PacketType.MapNodeEdit:
            {
                const edit = data as MapNodeEditRequest;
                const nodes = store.getters.mapNodes as Map<number, Node>;
                const tgNode = nodes.get(edit.id);
                if (tgNode)
                {
                    tgNode.name = edit.title;
                    tgNode.position = edit.position;
                    store.commit(Mutations.setMapNode, tgNode);
                    callbackArg = tgNode;
                }
                break;
            }
            case PacketType.Notification:
            {
                // TODO: Use a bootstrap alert
                alert(data.message);
                break;
            }
            case PacketType.AsyncResponse:
            {
                this.requests.handleResponsePacket(packet);
                break;
            }
            case PacketType.ProjectNotifyMetadataChanged:
            {
                store.commit(Mutations.updateProjectMetadata, data as ProjectNotifyMetadataChangedResponse);
                break;
            }
            case PacketType.SetFolder:
            {
                store.commit(Mutations.setFolder, data as SetFolderData);
                break;
            }
            case PacketType.DeleteFolder:
            {
                store.commit(Mutations.deleteFolder, data as DeleteFolderData);
                break;
            }
            case PacketType.TimelineSetFolder:
            {
                store.commit(Mutations.setTimelineFolder, data as TimelineSetFolderData);
                break;
            }
            case PacketType.FolderUpdateAccess:
            {
                store.commit(Mutations.setFolderAccess, data as FolderUpdateAccessData);
                break;
            }
            default:
                if (!this.handlers.has(type))
                    console.log("Unhandled packet type " + <PacketType>type);
                break;
            }
            
            // Pseudo-hack to force reactivity for node components
            if (packet && packet.data != null && packet.data.nodeid)
            {
                /** @type {Node} */
                const node = store.getters.nodes.get(packet.data.nodeid as number);
                if (node && node.component && node.component.forceUpdateRender)
                {
                    node.component.forceUpdateRender();
                }
            }

            // NOTE: I think the handlers system is essentially duplicated effort from this,
            // but I'm not sure if there's any value to keeping a Unity-styled system here.
            // I can't think of any benefit to the callbackArg approach vs just passing the packet
            // data through, but I'm keeping both around for now just in case.
            const list = this.getTypeListIfExists(type);
            if (list)
            {
                for(let i = 0; i < list.length; ++i)
                {
                    const cb = list[i];
                    if (cb)
                    {
                        cb(data, packet);
                    }
                }
            }

            if (this.handlers.has(type))
            {
                const handler = this.handlers.get(type);
                if (handler != undefined)
                {
                    handler.invoke(callbackArg);
                }
            }
        }
    }
}

// TODO: Move this into a better place
function simplifyAllowLatestWithNodeId(array : Packet[], index : number, pack : Packet, res : number[])
{
    console.log(`Testing packets of type ${pack.type} starting at index ${index}`);

    let lastIdx = index;
    let data = pack.data as {nodeid: number};
    for(let i = index + 1; i < array.length; ++i)
    {
        console.log(`Processing packet ${i}`);

        const cmpPack = array[i];
        if (cmpPack.type != pack.type) continue;
        const cmpData = cmpPack.data as {nodeid: number};
        if (cmpData.nodeid == data.nodeid)
        {
            console.log(`Removing packet at index ${lastIdx}`);
            res.push(lastIdx);
            data = cmpData;
            lastIdx = i;
        }
    }
}

ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateTitle, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateDescription, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateImage, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateImageCaption, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateAudio, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateVideo, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateStart, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateEnd, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateDateOptions, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeSetImportant, simplifyAllowLatestWithNodeId);
ProjectServer.setPacketTypeSimplifier(PacketType.NodeUpdateColor, simplifyAllowLatestWithNodeId);